/* global window */

const crpc = require('@cuvva/crpc');
const jsonClient = require('@cuvva/json-client');
const Cher = require('@cuvva/cher');
const { makeQuerablePromise } = require('./helpers/promise.js');
const mockedProdServiceEndpoints = require('./services.js');
const { toUrlBase64 } = require('./helpers/encoding.js');
const { LocalStore, MemoryStore } = require('./storage/index.js');

const defaultUrl = 'https://api.prod.cuv-prod.com/';

class CuvvaClient {
	constructor(options = {}) {
		this._opts = {
			// Default settings
			baseUrl: defaultUrl,
			authStorage: new MemoryStore(null),

			// Spread in specified options
			...options,
		};

		this._setupClients();
	}

	async changeEnvironment(environment) {
		const baseUrl = baseUrlFromEnvironment(environment);

		if (this._opts.baseUrl === baseUrl)
			return;

		this._opts.baseUrl = baseUrl;

		await this._doClientSetup();
	}

	async _setupClients() {
		if (this._setupClientsPromise && this._setupClientsPromise.isPending())
			return await this._setupClientsPromise;

		const promise = makeQuerablePromise(this._doClientSetup());

		return await (this._setupClientsPromise = promise);
	}

	async _doClientSetup() {
		let envConfig;
		const baseClient = crpc(this._opts.baseUrl);

		try {
			envConfig = await baseClient('1/service-env-config/1/2019-03-18/list_service_endpoints');
		} catch {
			if (this._opts.baseUrl !== defaultUrl) {
				// If this fails and we aren't on production then we should explicitly
				// set the _clients to null so we can throw a relevant error later.
				this._clients = null;

				return;
			}

			// As we're on production, we need to fallback to the copy of the service
			// endpoints that were baked in
			envConfig = mockedProdServiceEndpoints;
		}

		const clients = {};

		for (const key of Object.keys(envConfig.services)) {
			const service = envConfig.services[key];

			clients[key] = {
				rpc: crpc(service.primary),
				rest: jsonClient(service.primary),
			};
		}

		this._clients = clients;
		this._serviceEndpoints = envConfig.services;
	}

	async _getClient(service, path, type) {
		if (this._clients === null)
			throw new Cher('no_service_endpoints');

		if (!this._clients)
			await this._setupClients();

		const client = this._clients[service];

		if (!client)
			throw new Cher('unknown_service', { service });

		return client[type];
	}

	get auth() {
		return this._opts.authStorage.get();
	}

	set auth(auth) {
		if (auth && this._opts.clientId !== auth.clientId)
			throw new Cher('invalid_client_id');

		this._opts.authStorage.set(auth);
	}

	async _authenticate(grantType, options) {
		const payload = {
			clientId: this._opts.clientId,
			grantType,
			...options,
		};

		const response = await this.rpcNoAuth('service-auth', '2/2018-12-11/authenticate', payload);

		this.auth = response;
	}

	async _do(fn) {
		await this.refresh({ onlyWait: true });

		try {
			return await fn(this.auth);
		} catch (error) {
			if (error.code === 'token_not_found') {
				this.auth = null;

				throw error;
			}

			if (!['unauthorized', 'token_revoked'].includes(error.code))
				throw error;

			await this.refresh();

			return await fn(this.auth);
		}
	}

	async _doAuthenticate(grantType, options) {
		try {
			return await this._authenticate(grantType, options);
		} catch (error) {
			if (!this._opts.challengeHandler)
				throw error;

			const webauthnResponse = await this._opts.challengeHandler(this._opts.appId, error);

			return await this._authenticate(grantType, {
				...options,
				webauthnResponse,
			});
		}
	}

	async useAuthenticationCode(code, state) {
		if (!this._opts.localStorage)
			throw new Cher('local_storage_required');

		const authStates = this._opts.localStorage.get('authStates') || {};
		const authState = authStates[state];

		if (!authState)
			throw new Cher('invalid_state');

		const { codeVerifier, redirectUri } = authState;
		const options = {
			redirectUri: this._opts.redirectUri,
			codeVerifier,
			code,
		};

		await this._doAuthenticate('authorization_code', options);
		this._opts.localStorage.remove('authStates');

		return redirectUri;
	}

	async sendAuthenticationCode(email, env, redirectUri, build) {
		if (!this._opts.localStorage)
			throw new Cher('local_storage_required');

		const generated = await generateAuthorizationState(env, build);
		const authStates = {
			...this._opts.localStorage.get('authStates'),
			[generated.state]: {
				codeVerifier: generated.codeVerifier,
				redirectUri,
			},
		};

		this._opts.localStorage.set('authStates', authStates);

		const payload = {
			clientId: this._opts.clientId,
			responseType: 'code',
			redirectUri: this._opts.redirectUri,
			state: generated.state,
			codeChallengeMethod: 'S256',
			codeChallenge: generated.codeChallenge,
			email: email.trim(),
		};

		await this.rpcNoAuth('service-auth', '2/2018-12-11/send_authorization_code', payload);
	}

	async refresh(opts) {
		const options = {
			onlyWait: false,
			...opts,
		};

		// Check promise exists and that is hasn't been fulfilled
		if (this._authRefreshPromise && this._authRefreshPromise.isPending())
			return await this._authRefreshPromise;

		// If we only want to ensure any existing are done then break here
		if (options.onlyWait)
			return void 0;

		const promise = makeQuerablePromise(this._doAuthenticate('refresh_token', {
			refreshToken: this.auth.refreshToken,
		}));

		return await (this._authRefreshPromise = promise);
	}

	async createClientCode(device, handoff, scope) {
		await this.refresh({ onlyWait: true });

		const response = await this.rpcNoAuth(
			'service-auth',
			'2/2018-12-11/create_client_code', {
				clientId: this._opts.clientId,
				refreshToken: this.auth.refreshToken,
				scope,
				device,
				handoff,
			},
		);

		this.auth = {
			...this.auth,
			refreshToken: response.refreshToken,
		};

		return {
			...response,
			refreshToken: void 0,
		};
	}

	async rpc(service, path, body) {
		const client = await this._getClient(service, path, 'rpc');
		const fn = auth => client(path, body, generateOptions(auth));

		return await this._do(fn);
	}

	async rest(service, method, path, params, body) {
		const client = await this._getClient(service, path, 'rest');
		const fn = auth => client(method, path, params, body, generateOptions(auth));

		return await this._do(fn);
	}

	async rpcNoAuth(service, path, body) {
		const client = await this._getClient(service, path, 'rpc');

		return await client(path, body);
	}

	async restNoAuth(service, method, path, params, body) {
		const client = await this._getClient(service, path, 'rest');

		return await client(method, path, params, body);
	}
}

CuvvaClient.LocalStore = LocalStore;
CuvvaClient.MemoryStore = MemoryStore;

module.exports = CuvvaClient;

function baseUrlFromEnvironment(environment) {
	if (!environment || environment === 'prod')
		return defaultUrl;

	return `https://api.${environment}.cuv-nonprod.com/`;
}

function generateOptions(auth) {
	if (!auth)
		return void 0;

	let headers = {
		authorization: `bearer ${auth.accessToken}`,
	};

	headers = {
		...headers,
		'cuvva-client-version': 'web-1.0.0-0',
	};

	return { headers };
}

async function generateAuthorizationState(env, build) {
	const stateData = new Uint8Array(32);
	const verifierData = new Uint8Array(32);

	window.crypto.getRandomValues(stateData);
	window.crypto.getRandomValues(verifierData);

	const state = `${toUrlBase64(stateData)}|${env}${build ? `|${build}` : ''}`;
	const verifier = toUrlBase64(verifierData);
	const verifierParsedData = new window.TextEncoder('utf-8').encode(verifier);
	const challengeData = await window.crypto.subtle.digest('SHA-256', verifierParsedData);

	return {
		state,
		codeVerifier: verifier,
		codeChallenge: toUrlBase64(new Uint8Array(challengeData)),
	};
}
